// ChatRoom.tsx
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
    Text, Flex, useDisclosure, Spinner, Button, useColorModeValue
} from "@chakra-ui/react";
import useAuth from "../../../hooks/useAuth";
import { getSortedUsers, getOnlineUserIds } from "@utils/sortedUsers.ts";
import { useChatSocket } from "../../../hooks/useChatSocket";
import { MessageType, RoomService, MediaService } from "../../../client";
import { setActiveChatId } from "@utils/chatState.ts";

import FilePreviewModal from "../../../components/Modals/FilePreviewModal";
import UserList from "../../../components/Chat/UserList";
import MessageList from "../../../components/Chat/Shared/MessageList";
import ChatInput from "../../../components/Chat/Shared/ChatInput";
import ChatHeader from "../../../components/Chat/Shared/ChatHeader";
import { mapMessageTypeToChatMessage } from "../../../components/Chat/Shared/types";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import useLicenseActionGuard from "../../../hooks/useLicenseActionGuard";

export const Route = createFileRoute("/_layout/chat/$roomId")({
    component: ChatRoom,
});

function ChatRoom() {
    const { user } = useAuth();
    const chatWriteGuard = useLicenseActionGuard("self_service_write");
    const { roomId } = useParams({ from: "/_layout/chat/$roomId" });

    useEffect(() => {
        setActiveChatId(roomId ?? null);
        return () => setActiveChatId(null);
    }, [roomId]);

    const { data: room, isLoading, isError } = useQuery({
        queryKey: ["room", roomId],
        queryFn: () => RoomService.readRoomById(roomId),
        enabled: !!roomId,
    });

    const [messages, setMessages] = useState<MessageType[]>([]);
    const [input, setInput] = useState("");
    const [replyTo, setReplyTo] = useState<{
        id: string;
        user_id: string;
        full_name: string;
        message: string;
    } | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [filePreviews, setFilePreviews] = useState<{ name: string; size: string; preview: string; file: File }[]>([]);
    const [fileMessage, setFileMessage] = useState("");
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    const {
        isOpen: isFileModalOpen,
        onOpen: onFileModalOpen,
        onClose: onFileModalClose
    } = useDisclosure();

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxImages, setLightboxImages] = useState<string[]>([]);

    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const handleLoadMore = () => {
        if (!messages.length || !ws.current) return;
        setIsLoadingMore(true);
        ws.current.send(JSON.stringify({
            type: "load_more_messages",
            before: messages[0].id,
            limit: 25
        }));
    };

    const handleImageClick = (clickedUrl: string) => {
        const allImages = messages.flatMap((msg) =>
            (msg.content_url || []).filter((url) =>
                url.match(/\.(jpg|jpeg|png|gif|webp)$/i)
            )
        );
        const index = allImages.findIndex((url) => url === clickedUrl);
        if (index !== -1) {
            setLightboxImages(allImages);
            setLightboxIndex(index);
            setLightboxOpen(true);
        }
    };

    const isInteractionDisabled = !room || room.status === false || (room.is_channel && user?.ID !== room.owner_id) || chatWriteGuard.disabled;

    const sortedUsers = getSortedUsers(messages);
    const onlineIds = getOnlineUserIds(sortedUsers);

    const ws = useChatSocket({
        roomId,
        token: typeof window !== "undefined" ? localStorage.getItem("access_token") : null,
        user: user ? { ID: user.ID, fullName: user.fullName ?? "", avatar: user.avatar ?? "" } : null,
        onMessagesUpdate: (msgs) => setMessages(prev => {
            const newIds = new Set(msgs.map(m => m.id));
            return [...prev.filter(m => !newIds.has(m.id)), ...msgs];
        }),
        onNewMessage: (msg) => setMessages(prev => {
            const index = prev.findIndex(m => m.id === msg.id);
            if (index === -1) return [...prev, msg];
            const updated = [...prev];
            updated[index] = { ...msg, isLoading: false };
            return updated;
        }),
        onMessageUpdate: (data) => setMessages(prev => {
            const index = prev.findIndex(m => m.id === data.id);
            if (index === -1) return prev;
            const updated = [...prev];
            updated[index] = { ...prev[index], ...data, isLoading: false };
            return updated;
        }),
        onMessageDelete: (id) => setMessages(prev => prev.filter(m => m.id !== id)),
        onBatchMessages: (batch) => {
            const safeBatch = batch || [];
            setMessages(prev => [...safeBatch, ...prev]);
            setHasMoreMessages(safeBatch.length === 25);
            setIsLoadingMore(false);
        }
    });

    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);

    const handleEditMessage = (msg: any) => {
        setEditingMessageId(msg.id);
        setInput(msg.message);
        inputRef.current?.focus();
    };

    const sendMessage = () => {
        if (isInteractionDisabled || !user || !input.trim()) return;
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            if (editingMessageId) {
                ws.current.send(JSON.stringify({ type: "edit_message", id: editingMessageId, message: input }));
                setEditingMessageId(null);
            } else {
                ws.current.send(JSON.stringify({
                    id: crypto.randomUUID(),
                    user_id: user.ID,
                    full_name: user.fullName ?? "",
                    avatar: user.avatar ?? "",
                    room_id: roomId,
                    message: input,
                    isRead: false,
                    content_url: [],
                    created_at: new Date().toISOString(),
                    ...(replyTo ? {
                        reply_to: {
                            id: replyTo.id,
                            user_id: replyTo.user_id,
                            full_name: replyTo.full_name,
                            avatar: messages.find(m => m.id === replyTo.id)?.avatar || "",
                            message: replyTo.message,
                        },
                        reply_to_id: replyTo.id,
                    } : {}),
                }));
            }
            setInput("");
            setReplyTo(null);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || isInteractionDisabled || !user) return;
        const selectedFiles = Array.from(e.target.files);
        const previews = selectedFiles.map((file) => ({
            name: file.name,
            size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            preview: URL.createObjectURL(file),
            file,
        }));
        setFilePreviews(previews);
        setFiles(selectedFiles);
        onFileModalOpen();
    };

    const uploadSelectedFiles = async () => {
        if (!files.length || !user || isInteractionDisabled) return;
        const messageId = crypto.randomUUID();
        const formData = new FormData();
        files.forEach(file => formData.append("files", file));
        
        const placeholderMessage: MessageType = {
            id: messageId,
            user_id: user.ID,
            full_name: user.fullName ?? "",
            avatar: user.avatar ?? "",
            room_id: roomId,
            message: fileMessage,
            isRead: false,
            content_url: [],
            created_at: new Date().toISOString(),
            isLoading: true,
            ...(replyTo ? {
                reply_to: {
                    id: replyTo.id,
                    user_id: replyTo.user_id,
                    full_name: replyTo.full_name,
                    avatar: messages.find(m => m.id === replyTo.id)?.avatar || "",
                    message: replyTo.message,
                },
                reply_to_id: replyTo.id,
            } : {}),
        };

        setMessages(prev => [...prev, placeholderMessage]);
        ws.current?.send(JSON.stringify(placeholderMessage));
        onFileModalClose();

        try {
            await MediaService.downloadImages(messageId, formData);
            ws.current?.send(JSON.stringify({ type: "update_message", id: messageId }));
            setFiles([]);
            setFilePreviews([]);
            setFileMessage("");
        } catch (err) {
            console.error("Upload error:", err);
        }
    };

    const bgColor = useColorModeValue("gray.50", "gray.900");
    const chatBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    if (isLoading) return <Spinner size="xl" mx="auto" mt={12} />;
    if (isError || !room) return <Text textAlign="center" mt={12} color="red.500">Failed to load room</Text>;

    const mappedMessages = messages.map(mapMessageTypeToChatMessage);

    return (
        <Flex 
            h="calc(100dvh - 96px)"
            w="full"
            bg={bgColor}
            borderRadius="xl"
            overflow="hidden"
            boxShadow="sm"
            border="1px solid"
            borderColor={borderColor}
        >
            <UserList users={sortedUsers} onlineIds={onlineIds} />
            
            <Flex direction="column" flex="1" bg={chatBg} borderLeft="1px solid" borderColor={borderColor} minW={0}>
                <ChatHeader 
                    title={room.name_room} 
                    subtitle={room.status ? "Online" : "Closed"} 
                    avatarSrc={room.image}
                    showBackButton
                />

                <MessageList 
                    messages={mappedMessages}
                    currentUserId={user?.ID || ""}
                    onLoadMore={handleLoadMore}
                    hasMore={hasMoreMessages}
                    isLoadingMore={isLoadingMore}
                    onDelete={(id) => ws.current?.send(JSON.stringify({ type: "delete_message", id }))}
                    onEdit={handleEditMessage}
                    onImageClick={handleImageClick}
                    onReply={setReplyTo}
                />

                <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    index={lightboxIndex}
                    slides={lightboxImages.map((src) => ({ src }))}
                />

                {editingMessageId && (
                    <Flex px={4} py={1} bg="orange.50" align="center" justify="space-between">
                        <Text fontSize="xs" color="orange.600">Editing message...</Text>
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => { setEditingMessageId(null); setInput(""); }}>Cancel</Button>
                    </Flex>
                )}

                <ChatInput
                    ref={inputRef}
                    value={input}
                    onChange={setInput}
                    onSend={sendMessage}
                    onFileSelect={handleFileSelect}
                    disabled={isInteractionDisabled}
                    replyTo={replyTo}
                    onCancelReply={() => setReplyTo(null)}
                />

                <FilePreviewModal
                    isOpen={isFileModalOpen}
                    onClose={onFileModalClose}
                    files={filePreviews}
                    onRemove={(i) => {
                        const updated = [...filePreviews];
                        URL.revokeObjectURL(updated[i].preview);
                        updated.splice(i, 1);
                        setFilePreviews(updated);
                        setFiles(updated.map(f => f.file));
                    }}
                    onUpload={uploadSelectedFiles}
                    message={fileMessage}
                    onMessageChange={setFileMessage}
                    isDisabled={isInteractionDisabled}
                    onAddFiles={(newFiles) => {
                        const newPreviews = newFiles.map((file) => ({
                            name: file.name,
                            size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
                            preview: URL.createObjectURL(file),
                            file,
                        }));
                        setFilePreviews((prev) => [...prev, ...newPreviews]);
                        setFiles((prev) => [...prev, ...newFiles]);
                    }}
                />
            </Flex>
        </Flex>
    );
}
